import {
    Box,
    Grid,
    MenuItem,
    Select,
    Toolbar,
    useMediaQuery,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { themePalette } from "../config/theme.config";
import { useContext, useEffect, useState } from "react";
import { I18nContext } from "../I18nProvider";
import { RiAdminFill, RiLoginBoxLine, RiInformationLine } from "react-icons/ri";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { loginRequest } from "../redux/CAS/authConfig";
import { useMsal } from "@azure/msal-react";
import { inUser, outUser } from "../redux/slices/auth.slice";
import { callMsGraph } from "../redux/CAS/graph";
import ConsultasService from "../pages/Ranking/services/ConsultasService";
import Swal from "sweetalert2";

// 1. LA INTERFAZ VISUAL (Pura UI, sin lógica de MSAL)
const HeaderUI: React.FC<{ handleLoginAction: () => void; handleLogoutAction: () => void }> = ({ handleLoginAction, handleLogoutAction }) => {
    const theme = useTheme();
    const context = useContext(I18nContext);
    const isXSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { isAuth } = useAppSelector((state) => state.authReducer);
    const [openInfo, setOpenInfo] = useState(false);

    if (context === null) {
        throw new Error("The I18n context is not initialized. Make sure you have the provider set up correctly.");
    }

    return (
        <>
            <Box sx={{ flexGrow: 1, backgroundColor: themePalette.NAVCOLOR }}>
                <Toolbar>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid item xs={5} sm={4} md={3} lg xl>
                            <Box sx={{ m: 1, fontSize: { xs: "small", sm: "medium" } }}>
                                <h1 style={{ color: "white", cursor: "pointer" }} onClick={() => navigate("/")}>
                                    i-Research
                                </h1>
                            </Box>
                        </Grid>

                        <Grid item xs={7} sm={8} md={9}
                            sx={{ display: "flex", gap: 1.5, alignItems: "center", justifyContent: "flex-end" }}
                        >
                            <Tooltip title="Origen de los datos" placement="top">
                                <Button
                                    onClick={() => setOpenInfo(true)}
                                    startIcon={<RiInformationLine size={18} />}
                                    sx={{
                                        height: 40, color: themePalette.NAVCOLOR, backgroundColor: "white",
                                        textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2,
                                        border: "1px solid white",
                                        "&:hover": { backgroundColor: "#f0f0f0", borderColor: "#f0f0f0" },
                                        display: { xs: "none", sm: "flex" }, alignItems: "center",
                                    }}
                                >
                                    Origen de los datos
                                </Button>
                            </Tooltip>

                            <Select
                                value={context.language}
                                onChange={(e) => context.changeLanguage(e.target.value)}
                                sx={{
                                    height: 40, borderRadius: "10px", backgroundColor: "white",
                                    color: themePalette.NAVCOLOR, fontWeight: 600,
                                    "& .MuiOutlinedInput-notchedOutline": { border: "1px solid white" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f0f0f0" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f0f0f0" },
                                    "& .MuiSvgIcon-root": { color: themePalette.NAVCOLOR }, minWidth: 75,
                                }}
                            >
                                <MenuItem value={"es"}>Es</MenuItem>
                                <MenuItem value={"en"}>En</MenuItem>
                            </Select>

                            {!isAuth ? (
                                <Button
                                    onClick={handleLoginAction}
                                    startIcon={<RiLoginBoxLine size={18} />}
                                    sx={{
                                        height: 40, color: themePalette.NAVCOLOR, backgroundColor: "white",
                                        textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2,
                                        border: "1px solid white",
                                        "&:hover": { backgroundColor: "#f0f0f0", borderColor: "#f0f0f0" },
                                        display: "flex", alignItems: "center",
                                    }}
                                >
                                    Iniciar Sesión
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleLogoutAction}
                                    startIcon={<RiAdminFill size={18} />}
                                    sx={{
                                        height: 40, color: themePalette.NAVCOLOR, backgroundColor: "white",
                                        textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2,
                                        border: "1px solid white",
                                        "&:hover": {
                                            backgroundColor: "rgba(255,255,255,0.1)",
                                            borderColor: "white",
                                            color: "white !important",  
                                        },
                                        display: "flex", alignItems: "center",
                                    }}
                                >
                                    Cerrar Sesión
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Toolbar>
            </Box>

            <Dialog open={openInfo} onClose={() => setOpenInfo(false)} fullWidth maxWidth="sm">
                <DialogTitle>Origen de los datos</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" gutterBottom>
                        Los datos mostrados en este sistema provienen de las siguientes fuentes institucionales:
                    </Typography>
                    <ul style={{ paddingLeft: "1.2rem" }}>
                        <li>
                            <Typography variant="body2">
                                <strong>MySciVal</strong> – Plataforma de análisis de investigación de Elsevier.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                <strong>Scopus</strong> – Base de datos de resúmenes y citas de literatura científica.
                            </Typography>
                        </li>
                    </ul>
                    <Typography variant="body2" sx={{ mt: 2 }}>Links de referencia:</Typography>
                    <Typography variant="body2">• <a href="https://www.scival.com" target="_blank" rel="noreferrer">https://www.scival.com</a></Typography>
                    <Typography variant="body2">• <a href="https://www.scopus.com" target="_blank" rel="noreferrer">https://www.scopus.com</a></Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 3, fontStyle: "italic" }}>
                        Fecha de actualización: 2026
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenInfo(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// 2. EL CONTROLADOR DE MSAL (Solo se renderiza en Producción/Desarrollo)
const HeaderWithMsal: React.FC = () => {
    const { instance, accounts } = useMsal();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const getAccessToken = async () => {
        try {
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            });

            const userInfo = await callMsGraph(response.accessToken);
            const correo = userInfo.mail;

            if (correo.endsWith("@espol.edu.ec")) {
                const usuario = correo.split('@')[0];
                const data = await ConsultasService.Account("30", usuario);



                let jwtExpiration = "";
                if (data?.token) {
                    try {
                        const base64Url = data.token.split('.')[1];
                        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                        const payload = JSON.parse(window.atob(base64));
                        if (payload.exp) jwtExpiration = payload.exp.toString();
                    } catch (error) {
                        console.error("Error al decodificar el JWT:", error);
                    }
                }

                const roles = data?.user?.roles || [];
                const rolEncontrado = roles.find((rol: any) => rol.nombreRol === "ADMIN RANKING INVESTIGACIÓN") ||
                                      roles.find((rol: any) => rol.nombreRol === "LECTOR RANKING INVESTIGACIÓN");

                                      if (rolEncontrado) {
                    dispatch(inUser({
                        userData: {
                            usuario: usuario,
                            token: data.token,
                            expiration: jwtExpiration || response.expiresOn?.toString() || ""
                        },
                        rol: rolEncontrado.nombreRol,
                        token: data.token,
                        isAuth: true,
                        isLoading: false
                    }));
                } else {
                    // EL USUARIO SE VALIDÓ EN MICROSOFT, PERO NO TIENE ROLES EN LA API
                    Swal.fire({
                        icon: 'error',
                        title: 'Acceso Denegado',
                        text: 'No tienes los permisos necesarios para ingresar al sistema de Ranking.',
                        confirmButtonColor: '#243165'
                    }).then(() => {
                        dispatch(outUser());
                        instance.logoutRedirect({ account: accounts[0] });
                    });
                }
            } else {
                // EL CORREO NO ES @ESPOL.EDU.EC
                Swal.fire({
                    icon: 'warning',
                    title: 'Correo inválido',
                    text: 'Debes usar un correo institucional válido (@espol.edu.ec).',
                    confirmButtonColor: '#243165'
                }).then(() => {
                    dispatch(outUser());
                    instance.logoutRedirect({ account: accounts[0] });
                });
            }
        } catch (error) {
            console.log("Error al obtener el access_token:", error);
        }
    };

    useEffect(() => {
        if (accounts.length > 0) getAccessToken();
    }, [accounts]);

    const handleLoginAction = (): void => {
        instance.loginRedirect(loginRequest).catch((e) => {
            console.error("Error al intentar iniciar sesión con Microsoft:", e);
        });
    };

    const handleLogoutAction = () => {
        dispatch(outUser());
        if (accounts.length > 0) {
            instance.logoutRedirect({
                account: accounts[0]
            }).catch((e) => console.log("Error en MSAL logout:", e));
        } else {
            navigate("/");
        }
    };

    return <HeaderUI handleLoginAction={handleLoginAction} handleLogoutAction={handleLogoutAction} />;
};

// 3. EL COMPONENTE PRINCIPAL (Decide qué versión renderizar)
export const HeaderBar: React.FC = () => {
    const disableMsal = process.env.REACT_APP_DISABLE_MSAL === 'true';
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    if (disableMsal) {
        const handleNavigationLogin = () => {
            navigate("/login");
        };

        const handleMockLogout = () => {
            dispatch(outUser());
            navigate("/");
        };

        return <HeaderUI handleLoginAction={handleNavigationLogin} handleLogoutAction={handleMockLogout} />;
    }

    return <HeaderWithMsal />;
};